import React, { useState, useEffect } from 'react';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useIsEditor } from '../hooks/useIsEditor';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Snackbar
} from '@mui/material';
import {
  Person,
  Security,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';

const ApproversManagement = ({ 
  open, 
  onClose, 
  account, 
  onUpdate,
  authenticatedFetch,
  API_ENDPOINT 
}) => {
  const isAdmin = useIsAdmin();
  const isEditor = useIsEditor();
  const canEdit = isAdmin || isEditor;
  const [loading, setLoading] = useState(false);
  const [allApprovers, setAllApprovers] = useState({});
  const [newApprovers, setNewApprovers] = useState('');
  const [validatedUsers, setValidatedUsers] = useState([]);
  const [validating, setValidating] = useState(false);
  const [updateAction, setUpdateAction] = useState('replace');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedLevels, setSelectedLevels] = useState([]);

  // Load all access levels approvers when dialog opens
  useEffect(() => {
    if (open && account) {
      loadAllApprovers();
    }
  }, [open, account]);

  const loadAllApprovers = async () => {
    setLoading(true);
    
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${account.id}/approvers/all`
      );
      const data = await response.json();
      
      if (data.approvers_by_level) {
        setAllApprovers(data.approvers_by_level);
        // Initialize selectedLevels to all keys (all checked by default)
        setSelectedLevels(Object.keys(data.approvers_by_level));
      } else {
        setSnackbar({ open: true, message: 'Failed to load approvers data', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Error loading approvers: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validateUsernames = async () => {
    if (!newApprovers.trim()) {
      setValidatedUsers([]);
      return;
    }

    setValidating(true);

    try {
      // Parse usernames (comma or space separated)
      const usernames = newApprovers
        .split(/[,\s]+/)
        .map(u => u.trim())
        .filter(u => u.length > 0);

      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${account.id}/approvers/validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ usernames })
        }
      );

      const data = await response.json();
      setValidatedUsers(data.validated_users || []);
    } catch (err) {
      setSnackbar({ open: true, message: `Error validating usernames: ${err.message}`, severity: 'error' });
    } finally {
      setValidating(false);
    }
  };

  const updateApprovers = async () => {
    const validUsers = validatedUsers.filter(u => u.valid);
    
    if (validUsers.length === 0) {
      setSnackbar({ open: true, message: 'No valid users to update', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const usernames = validUsers.map(u => u.user_name);
      
      // Build request body — omit access_levels when all selected (backward compat)
      const body = {
        approvers: usernames,
        action: updateAction
      };
      if (!allSelected) {
        body.access_levels = selectedLevels;
      }

      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${account.id}/approvers`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: `Successfully updated ${result.updated_records_count} access level records`, severity: 'success' });
        
        // Reload approvers data
        await loadAllApprovers();
        
        // Notify parent component
        if (onUpdate) {
          onUpdate();
        }
        
        // Clear form
        setNewApprovers('');
        setValidatedUsers([]);
        
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to update approvers', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Error updating approvers: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewApprovers('');
    setValidatedUsers([]);
    setSelectedLevels([]);
    setSnackbar(prev => ({ ...prev, open: false }));
    onClose();
  };

  const getAccessLevelIcon = (level) => {
    switch (level) {
      case 'level_1':
        return <Security sx={{ fontSize: 14, color: 'secondary.main' }} />;
      case 'level_2':
        return <Security sx={{ fontSize: 14, color: 'primary.main' }} />;
      case 'security_audit':
        return <Security sx={{ fontSize: 14, color: 'error.main' }} />;
      case 'db_admin':
        return <Security sx={{ fontSize: 14, color: 'info.main' }} />;
      case 'read_only':
        return <Security sx={{ fontSize: 14, color: 'success.main' }} />;
      case 'other':
        return <Person sx={{ fontSize: 14, color: 'action.active' }} />;
      default:
        return <Person sx={{ fontSize: 14, color: 'action.active' }} />;
    }
  };

  const getAccessLevelColor = (level) => {
    switch (level) {
      case 'level_1': return 'secondary';
      case 'level_2': return 'primary';
      case 'security_audit': return 'error';
      case 'db_admin': return 'info';
      case 'read_only': return 'success';
      case 'other': return 'default';
      default: return 'default';
    }
  };

  // Define the display order for access levels
  const getAccessLevelOrder = (level) => {
    const order = {
      'level_1': 1,
      'level_2': 2,
      'security_audit': 3,
      'db_admin': 4,
      'read_only': 5,
      'other': 6
    };
    return order[level] || 999; // Unknown levels go to the end
  };

  const totalCurrentApprovers = Object.values(allApprovers).reduce(
    (total, approvers) => total + approvers.length, 0
  );

  const sortedLevels = Object.entries(allApprovers)
    .sort(([a], [b]) => getAccessLevelOrder(a) - getAccessLevelOrder(b));

  // Derive allLevelKeys sorted by access level order
  const allLevelKeys = Object.keys(allApprovers)
    .sort((a, b) => getAccessLevelOrder(a) - getAccessLevelOrder(b));

  // Select All toggle state derivation
  const allSelected = selectedLevels.length === allLevelKeys.length && allLevelKeys.length > 0;
  const noneSelected = selectedLevels.length === 0;
  const indeterminate = !allSelected && !noneSelected;

  // Select All toggle handler
  const handleSelectAllToggle = () => {
    if (allSelected) {
      setSelectedLevels([]);
    } else {
      setSelectedLevels([...allLevelKeys]);
    }
  };

  // Individual checkbox toggle handler
  const handleLevelToggle = (level) => {
    setSelectedLevels(prev =>
      prev.includes(level)
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  // Update button label (Task 3.4)
  const getButtonLabel = () => {
    if (allSelected) return `Update All Access Levels (${updateAction})`;
    return `Update ${selectedLevels.length} Access Levels (${updateAction})`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '0.95rem', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Security sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="body1" fontWeight={600}>
            Manage Account Approvers
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          Account: {account?.name} ({account?.id})
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>

        {/* Inline loading indicator */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Loading approvers...</Typography>
          </Box>
        )}

        {/* Current Approvers by Access Level */}
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 0.5, mt: loading ? 1 : 0 }}>
          Current Access Levels and Approvers
          {totalCurrentApprovers > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', ml: 0.5 }}>
              ({Object.keys(allApprovers).length} levels, {totalCurrentApprovers} total)
            </Typography>
          )}
        </Typography>

        {Object.keys(allApprovers).length === 0 && !loading ? (
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>No approvers found for this account</Alert>
        ) : (
          <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 260, overflow: 'auto', mb: 2 }}>
            {sortedLevels.map(([accessLevel, approvers], groupIdx) => (
              <React.Fragment key={accessLevel}>
                {/* Access level header row */}
                <ListItem sx={{ px: 1, py: 0.5, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
                    {getAccessLevelIcon(accessLevel)}
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      {accessLevel.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    <Chip
                      label={`${approvers.length}`}
                      size="small"
                      color={getAccessLevelColor(accessLevel)}
                      sx={{ height: 16, fontSize: '0.65rem' }}
                    />
                  </Box>
                </ListItem>
                {/* Approvers in this level */}
                {approvers.map((approver, idx) => (
                  <ListItem key={idx} sx={{ px: 2, py: 0.25, borderBottom: idx < approvers.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                          {approver.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {approver.user_name}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                {groupIdx < sortedLevels.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Access Level Selector — Task 3.2, 3.3 */}
        {allLevelKeys.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              Select Access Levels to Update
            </Typography>

            {/* Select All / Deselect All toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={indeterminate}
                  onChange={handleSelectAllToggle}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Typography>
              }
              sx={{ mb: 0.5 }}
            />
            <Divider sx={{ mb: 0.75 }} />

            {/* Individual access level checkboxes — 2-column grid */}
            <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              {allLevelKeys.map((level) => (
                <FormControlLabel
                  key={level}
                  control={
                    <Checkbox
                      checked={selectedLevels.includes(level)}
                      onChange={() => handleLevelToggle(level)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                      {level.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Bulk Update Section */}
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 0.5 }}>
          Update Approvers
        </Typography>

        {!noneSelected && (
          <Alert severity={allSelected ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              {allSelected ? (
                <><strong>Warning:</strong> This will update approvers for <strong>ALL {allLevelKeys.length} access levels</strong> simultaneously.</>
              ) : (
                <>This will update approvers for <strong>{selectedLevels.length} access level{selectedLevels.length > 1 ? 's' : ''}</strong>: {selectedLevels.map(l => l.replace(/_/g, ' ').toUpperCase()).join(', ')}</>
              )}
            </Typography>
          </Alert>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel sx={{ fontSize: '0.85rem' }}>Update Action</InputLabel>
          <Select
            value={updateAction}
            onChange={(e) => setUpdateAction(e.target.value)}
            label="Update Action"
            sx={{ fontSize: '0.85rem' }}
          >
            <MenuItem value="replace" sx={{ fontSize: '0.85rem' }}>Replace all approvers</MenuItem>
            <MenuItem value="add" sx={{ fontSize: '0.85rem' }}>Add to existing approvers</MenuItem>
            <MenuItem value="remove" sx={{ fontSize: '0.85rem' }}>Remove from existing approvers</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          label="User IDs (comma or space separated)"
          placeholder="I70625, I21893, I70794, I21324"
          value={newApprovers}
          onChange={(e) => setNewApprovers(e.target.value)}
          sx={{
            mb: 1,
            '& .MuiInputBase-input': { fontSize: '0.85rem' },
            '& .MuiFormHelperText-root': { fontSize: '0.75rem' }
          }}
          helperText="Enter Verisk user IDs (e.g., I70625). Separate multiple IDs with commas or spaces."
        />

        <Box sx={{ mb: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={validateUsernames}
            disabled={!newApprovers.trim() || validating}
            sx={{ fontSize: '0.78rem' }}
          >
            {validating ? <CircularProgress size={13} sx={{ mr: 0.5 }} /> : null}
            {validating ? 'Validating...' : 'Validate User IDs'}
          </Button>
        </Box>

        {/* Validation Results */}
        {validatedUsers.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              Validation Results:
            </Typography>
            {validatedUsers.map((user, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                {user.valid ? (
                  <CheckCircle sx={{ fontSize: 13, color: 'success.main' }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 13, color: 'error.main' }} />
                )}
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  <strong>{user.user_name}</strong>
                  {user.valid ? (
                    <span> → {user.name}</span>
                  ) : (
                    <span style={{ color: 'red' }}> → {user.error}</span>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={handleClose} size="small">Cancel</Button>
        {canEdit && (
          <Button
            variant="contained"
            size="small"
            onClick={updateApprovers}
            disabled={
              loading || 
              validatedUsers.length === 0 || 
              !validatedUsers.some(u => u.valid) ||
              noneSelected
            }
            color="primary"
            sx={{ fontSize: '0.75rem' }}
          >
            {loading ? <CircularProgress size={13} sx={{ mr: 0.5, color: 'inherit' }} /> : null}
            {loading ? 'Updating...' : getButtonLabel()}
          </Button>
        )}
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', fontSize: '0.75rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ApproversManagement;
